import { noteExpectedFailure } from '@utils/observability/expectedFailure';

/*
 * IPC rejections that are NOT failures: the installed Electron shell predates the channel.
 *
 * A shell without the handler rejects `invoke` with "No handler registered for '<channel>'".
 * Measured 2026-09-02 on Sentry issue 7523204254 (`getPrinters`): 7,776 events since 2026-06-03,
 * `print_trigger` 7744/7744 `init` — i.e. every single one fired at mount, none from the manual
 * "Actualizar lista" button. And they arrive without triage value: 10/10 events sampled that day
 * carried `commerce_id: null`, because the hook runs in `Layout` before the commerce store loads.
 * Nobody in this repo can register a handler either — that lives in `plick-desktop`.
 *
 * Careful with what the tags do and do NOT prove: `browser` is 100% `Electron 24.8.8`, but that is
 * the runtime pinned in `plick-desktop/package.json`, constant across every app version, so its
 * zero variance says nothing about which build is installed. Issue 7524332195 shows a shell on that
 * same Electron reporting `desktop_app_version: 1.1.6` where `getPrinters` DOES exist
 * (`printers_enumerated: 6`).
 */

// True when the rejection comes from a channel the shell does not expose (outdated shell) rather
// than from the operation itself. Reads `message` with a `String(error)` fallback: Electron always
// rejects with an Error, but this runs inside a catch and must never throw.
export const isMissingIpcHandler = (error: unknown): boolean =>
  String((error as { message?: unknown } | null | undefined)?.message ?? error).includes('No handler registered');

// Records the outdated shell as a breadcrumb instead of an event. Lowering the level to `warning`
// would NOT help: a warning still opens the issue and still counts every event. The breadcrumb
// travels attached to any REAL error later in the session, which is where it pays off —
// `printer_not_selected` reads very differently once you know `printers_enumerated: 0` means a
// shell that cannot enumerate, not an operator who never picked a printer.
export const noteMissingIpcHandler = (channel: string): void => {
  noteExpectedFailure('electron.ipc', `IPC handler '${channel}' missing - outdated desktop shell`, { channel });
};
